import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fetchGallerier, fetchGalleriBilleder, fetchDedicatedGallery, fetchFoerEfterCases, parseImageCaptions } from '../lib/cms';
import { imageUrl } from '../lib/images';
import AlbumModal from './AlbumModal';

export default function InteriorGallery() {
  const [galleries, setGalleries] = useState([]);
  const [dedicatedBilleder, setDedicatedBilleder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmsBeforeAfterCases, setCmsBeforeAfterCases] = useState([]);
  const [activeCategory, setActiveCategory] = useState('alle'); // 'alle', 'koekken', 'borde', 'erhverv'
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAlbumIndex, setSelectedAlbumIndex] = useState(null);

  // Før/Efter interaktiv slider state
  const [activeCaseIndex, setActiveCaseIndex] = useState(0);
  const [sliderPosition, setSliderPosition] = useState(50); // 0 to 100
  const [isDragging, setIsDragging] = useState(false);
  const sliderContainerRef = useRef(null);
  const gallerySectionRef = useRef(null);

  // Touch handling for lightbox swipe
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);

  const PAGE_SIZE = 12;

  // Default / fallback før/efter cases
  const defaultBeforeAfterCases = useMemo(() => [
    {
      id: 'koekken-skaerbaek',
      title: 'Køkkenforvandling',
      location: 'Skærbæk',
      tag: 'Køkken & Badeværelse',
      description: 'Gammelt, slidt hvidt køkken forvandlet til et eksklusivt, moderne designkøkken med mat koksgrå/sort kvalitetsfolie.',
      benefits: ['Ingen byggerod eller nedrivning', 'Udført på under 2 dage', 'Spar op mod 70% ift. nyt køkken'],
      beforeImage: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/foer.jpeg',
      afterImage: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/efter.jpeg',
      beforeAlt: 'Køkken før foliemontering med slidte hvide låger',
      afterAlt: 'Køkken efter montering af eksklusiv mørk interiørfolie'
    },
    {
      id: 'spisebord-vejen',
      title: 'Spisebord & Møbeloverflade',
      location: 'Vejen',
      tag: 'Borde, Møbler & Skabe',
      description: 'Slidt hvid bordplade renoveret med autentisk mørk træstrukturfolie. Ekstremt slidstærk og rengøringsvenlig.',
      benefits: ['Varme- og ridsefast overflade', 'Naturtro træstruktur med taktil følelse', 'Giver elskede møbler nyt liv'],
      beforeImage: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/foer-skab.jpeg',
      afterImage: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/efter-bord.jpeg',
      beforeAlt: 'Møbel/bord før folie',
      afterAlt: 'Spisebord beklædt med luksus mørk træfolie'
    },
    {
      id: 'garderobeskab-montering',
      title: 'Klædeskab & Fronter',
      location: 'Rødding / Kolding',
      tag: 'Garderobe & Døre',
      description: 'Standard hvidt klædeskab renoveret med moderne mørk overflade, der passer perfekt til rummets øvrige inventar.',
      benefits: ['Ensartet udtryk i soveværelset', 'Nem aftørring og rengøring', 'Holdbarhed i mange år'],
      beforeImage: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/kl-c3-a6deskab-f-c3-b8r_1.webp',
      afterImage: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/b34e536e-8918-40a8-ba14-dc61324335f6.jpg',
      beforeAlt: 'Klædeskab før foliemontering',
      afterAlt: 'Klædeskab fornyet med mat interiørfolie'
    }
  ], []);

  // Hent alle gallerier, dedikerede billeder og før/efter cases fra CMS
  useEffect(() => {
    async function loadInteriorData() {
      setLoading(true);
      try {
        const [allGalleries, cases, koekkenItems, bordeItems, erhvervItems, oevrigeItems, oldInteriorItems, allBilleder] = await Promise.all([
          fetchGallerier(),
          fetchFoerEfterCases(),
          fetchDedicatedGallery('galleri_interioer_koekken', 'interioerfolie-koekken'),
          fetchDedicatedGallery('galleri_interioer_borde_moebler', 'interioerfolie-borde-moebler'),
          fetchDedicatedGallery('galleri_interioer_erhverv', 'interioerfolie-erhverv'),
          fetchDedicatedGallery('galleri_interioer_oevrige', 'oevrige'),
          fetchDedicatedGallery('galleri_interioerfolie', 'interioerfolie'),
          fetchGalleriBilleder()
        ]);
        setGalleries(allGalleries || []);
        if (cases && cases.length > 0) {
          setCmsBeforeAfterCases(cases);
        }

        // Tag billeder fra de 4 nye dedikerede samlinger:
        const mappedKoekken = (koekkenItems || []).map((b) => ({ ...b, galleri_slug: 'interioerfolie-koekken', kategori: 'Køkken & Badeværelse' }));
        const mappedBorde = (bordeItems || []).map((b) => ({ ...b, galleri_slug: 'interioerfolie-borde-moebler', kategori: 'Borde, Møbler & Skabe' }));
        const mappedErhverv = (erhvervItems || []).map((b) => ({ ...b, galleri_slug: 'interioerfolie-erhverv', kategori: 'Erhverv, Kantiner & Døre' }));
        const mappedOevrige = (oevrigeItems || []).map((b) => ({ ...b, galleri_slug: 'oevrige', kategori: 'Øvrige forvandlinger' }));

        let interiorDedicated = [...mappedKoekken, ...mappedBorde, ...mappedErhverv, ...mappedOevrige];

        // Tjek 'galleri_interioerfolie' for ekstra elementer (f.eks. forsamlingshus)
        if (oldInteriorItems && oldInteriorItems.length > 0) {
          oldInteriorItems.forEach((b) => {
            if (!interiorDedicated.some((existing) => existing.id === b.id || (existing.url && existing.url === b.url))) {
              interiorDedicated.push({ ...b, galleri_slug: 'interioerfolie-oevrige', kategori: 'Øvrige forvandlinger' });
            }
          });
        }

        // Fallback hvis de nye samlinger mod forventning er tomme
        if (interiorDedicated.length === 0 && oldInteriorItems && oldInteriorItems.length > 0) {
          interiorDedicated = oldInteriorItems.map((item) => {
            let galleri_slug = item.galleri_slug || 'interioerfolie';
            const kat = (item.kategori || '').toLowerCase();
            if (kat.includes('øvrig') || kat.includes('oevrig') || kat.includes('andre') || kat.includes('diverse')) {
              galleri_slug = 'oevrige';
            } else if (kat.includes('køkken') || kat.includes('bad')) {
              galleri_slug = 'interioerfolie-koekken';
            } else if (kat.includes('bord') || kat.includes('møbel') || kat.includes('skab')) {
              galleri_slug = 'interioerfolie-borde-moebler';
            } else if (kat.includes('erhverv') || kat.includes('kantine') || kat.includes('dør')) {
              galleri_slug = 'interioerfolie-erhverv';
            } else if (galleri_slug === 'interioerfolie') {
              galleri_slug = 'oevrige';
            }
            return {
              ...item,
              galleri_slug
            };
          });
        }

        // Undgå dobbelt-visning: Saml unikt baseret på id og primær billed-URL
        const deduplicatedBilleder = [];
        const seenIds = new Set();
        const seenUrls = new Set();

        const addUnique = (item) => {
          if (!item) return;
          const itemId = item.id;
          const primaryUrl = (item.images && item.images[0]) || item.url;
          if (itemId && seenIds.has(itemId)) return;
          if (primaryUrl && seenUrls.has(primaryUrl)) return;
          if (itemId) seenIds.add(itemId);
          if (primaryUrl) seenUrls.add(primaryUrl);
          deduplicatedBilleder.push(item);
        };

        interiorDedicated.forEach(addUnique);

        // Hvis der er specifikke interiør-elementer i allBilleder som ikke findes i de dedikerede, tag dem med uden dubletter
        if (allBilleder && allBilleder.length > 0) {
          allBilleder
            .filter((b) => {
              const slug = (b.galleri_slug || '').toLowerCase();
              const kat = (b.kategori || '').toLowerCase();
              return slug.includes('interioer') || kat.includes('interiør') || kat.includes('køkken') || kat.includes('bord');
            })
            .forEach(addUnique);
        }

        setDedicatedBilleder(deduplicatedBilleder);
      } catch (err) {
        console.error('Fejl ved hentning af interiør data:', err);
      } finally {
        setLoading(false);
      }
    }
    loadInteriorData();
  }, []);

  // Før/Efter showcases: brug data fra CMS hvis til stede, ellers fallback
  const beforeAfterCases = useMemo(() => {
    return cmsBeforeAfterCases.length > 0 ? cmsBeforeAfterCases : defaultBeforeAfterCases;
  }, [cmsBeforeAfterCases, defaultBeforeAfterCases]);

  const activeCase = beforeAfterCases[activeCaseIndex] || beforeAfterCases[0];

  // Håndtering af Before/After slider bevægelse
  const updateSliderFromClientX = (clientX) => {
    if (!sliderContainerRef.current) return;
    const rect = sliderContainerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setSliderPosition(percentage);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    updateSliderFromClientX(e.clientX);
  };

  const handleMouseMove = (e) => {
    // Hvis brugeren trækker ELLER blot bevæger musen hen over (hvis det ønskes)
    if (isDragging) {
      updateSliderFromClientX(e.clientX);
    }
  };

  const handleContainerMouseMove = (e) => {
    // Giver den intuitive effekt brugeren efterspurgte: "Eller scrolle musen hen - før efter"
    if (!isDragging && sliderContainerRef.current) {
      updateSliderFromClientX(e.clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    setIsDragging(true);
    if (e.touches && e.touches[0]) {
      updateSliderFromClientX(e.touches[0].clientX);
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && e.touches && e.touches[0]) {
      updateSliderFromClientX(e.touches[0].clientX);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Hjælpefunktion til at detektere om et billede er et "Før" eller "Efter" billede
  const getPhotoType = (url) => {
    const lower = url.toLowerCase();
    if (lower.includes('foer') || lower.includes('før')) return 'foer';
    if (lower.includes('efter')) return 'efter';
    return 'neutral';
  };

  // Hjælpefunktion til dynamisk valg af ikon for interiør-kategorier
  const getCategoryIcon = (title = '', slug = '') => {
    const s = (title + ' ' + slug).toLowerCase();
    if (s.includes('køkken') || s.includes('koekken') || s.includes('bad')) return 'fa-kitchen-set';
    if (s.includes('bord') || s.includes('møbel') || s.includes('moebel') || s.includes('stol') || s.includes('reol')) return 'fa-chair';
    if (s.includes('erhverv') || s.includes('kantine') || s.includes('kontor') || s.includes('bygning')) return 'fa-building';
    if (s.includes('skab') || s.includes('garderobe') || s.includes('front')) return 'fa-door-closed';
    if (s.includes('dør') || s.includes('doer') || s.includes('vindu')) return 'fa-door-open';
    if (s.includes('trappe')) return 'fa-stairs';
    return 'fa-couch';
  };

  // Hent og strukturer alle interiør-kategorier dynamisk fra CMS'et
  // Kunden vælger kategorien for et billede ved at uploade/placere det i det pågældende galleri i CMS
  const { categories, allImages, imagesByCategory } = useMemo(() => {
    // Find alle gallerier der tilhører interiørfolie
    const interiorList = galleries.filter((g) => {
      const isTerm = g.terms?.['galleri-kategori']?.includes('interioerfolie');
      const slug = (g.side_slug || g.page_slug || '').toLowerCase();
      return isTerm || slug.startsWith('interioerfolie') || slug.includes('interioer');
    });

    // Del op i specifikke underkategorier (f.eks. interioerfolie-koekken, interioerfolie-borde-moebler, interioerfolie-erhverv mv.)
    const subGalleries = interiorList.filter((g) => {
      const slug = (g.side_slug || g.page_slug || '').toLowerCase();
      return slug !== 'interioerfolie';
    });

    const fallbackGallery = interiorList.find((g) => {
      const slug = (g.side_slug || g.page_slug || '').toLowerCase();
      return slug === 'interioerfolie';
    });

    const byCat = {};
    const catDefs = [];
    const allImgsList = [];
    const seenUrls = new Set();

    if (subGalleries.length > 0) {
      subGalleries.forEach((gallery) => {
        const catId = gallery.side_slug || gallery.page_slug || gallery.id;
        const catTitle = gallery.titel || gallery.title || 'Kategori';

        // Tjek om der findes dedikerede billeder i de dedikerede samlinger for denne kategori
        const dedicatedForCat = dedicatedBilleder.filter(
          (b) => {
            const bSlug = (b.galleri_slug || '').toLowerCase();
            const bKat = (b.kategori || '').toLowerCase();
            if (catId === 'interioerfolie-koekken') {
              return bSlug.includes('koekken') || bKat.includes('køkken');
            }
            if (catId === 'interioerfolie-borde-moebler') {
              return bSlug.includes('borde') || bSlug.includes('moebler') || bKat.includes('borde') || bKat.includes('møbler');
            }
            if (catId === 'interioerfolie-erhverv') {
              return bSlug.includes('erhverv') || bKat.includes('erhverv') || bKat.includes('kantine');
            }
            if (catId === 'oevrige' || catId === 'interioerfolie-oevrige') {
              return bSlug.includes('oevrige') || bKat.includes('øvrig') || bKat.includes('forsamlingshus');
            }
            return b.galleri_slug === catId || b.galleri_slug === gallery.side_slug;
          }
        );

        let mappedImages = [];
        if (dedicatedForCat.length > 0) {
          mappedImages = dedicatedForCat.map((b, idx) => {
            const photoType = getPhotoType(b.url);
            const imgs = (b.images && b.images.length > 0) ? b.images : (b.url ? [b.url] : []);
            const projectTitle = b.titel || b.title || (photoType === 'foer' ? 'Før folie' : photoType === 'efter' ? 'Efter folie' : catTitle);
            return {
              url: imgs[0] || b.url,
              images: imgs,
              imageCount: imgs.length,
              isAlbum: imgs.length > 1,
              id: b.id || `${catId}-${idx}`,
              category: catId,
              categoryLabel: catTitle,
              projectTitle,
              title: projectTitle,
              photoType,
              badgeLabel: photoType === 'foer' ? 'FØR' : photoType === 'efter' ? 'EFTER' : (gallery.badge_tekst || catTitle),
              description: b.beskrivelse || gallery.beskrivelse || gallery.description || '',
              sortering: typeof b.sortering === 'number' ? b.sortering : idx
            };
          });
        } else {
          const rawImages = gallery.images || gallery.billeder || [];
          const captions = parseImageCaptions(gallery.billedtekster, rawImages.length);

          mappedImages = rawImages.map((url, idx) => {
            const photoType = getPhotoType(url);
            const cap = captions[idx];
            const defaultTitle = photoType === 'foer' ? 'Før folie' : photoType === 'efter' ? 'Efter folie' : catTitle;
            const projectTitle = cap?.title || defaultTitle;
            return {
              url,
              images: [url],
              imageCount: 1,
              isAlbum: false,
              id: `${catId}-${idx}`,
              category: catId,
              categoryLabel: catTitle,
              projectTitle,
              title: projectTitle,
              photoType,
              badgeLabel: photoType === 'foer' ? 'FØR' : photoType === 'efter' ? 'EFTER' : (gallery.badge_tekst || catTitle),
              description: cap?.description || gallery.beskrivelse || gallery.description || '',
              sortering: idx
            };
          });
        }

        byCat[catId] = mappedImages;

        // Føj til samlet oversigt (undgå duplikerede billeder på tværs)
        mappedImages.forEach((img) => {
          if (!seenUrls.has(img.url)) {
            seenUrls.add(img.url);
            allImgsList.push(img);
          }
        });

        catDefs.push({
          id: catId,
          label: catTitle,
          sublabel: gallery.beskrivelse || gallery.description || 'Se forvandlinger',
          icon: getCategoryIcon(catTitle, catId),
          count: mappedImages.length
        });
      });

      // Tjek om der i det overordnede interiørgalleri findes ekstra billeder eller dedikerede billeder til 'Øvrige forvandlinger'
      const dedicatedOevrige = dedicatedBilleder.filter(
        (b) =>
          b.galleri_slug === 'oevrige' ||
          b.galleri_slug === 'interioerfolie' ||
          (b.kategori || '').toLowerCase().includes('øvrig') ||
          (b.kategori || '').toLowerCase().includes('oevrig')
      );

      const extraImages = [];
      if (dedicatedOevrige.length > 0) {
        dedicatedOevrige.forEach((b, idx) => {
          if (!seenUrls.has(b.url)) {
            seenUrls.add(b.url);
            const photoType = getPhotoType(b.url);
            const imgs = (b.images && b.images.length > 0) ? b.images : (b.url ? [b.url] : []);
            const projectTitle = b.titel || b.title || (photoType === 'foer' ? 'Før folie' : photoType === 'efter' ? 'Efter folie' : 'Interiøropgave');
            const imgObj = {
              url: imgs[0] || b.url,
              images: imgs,
              imageCount: imgs.length,
              isAlbum: imgs.length > 1,
              id: b.id || `oevrige-${idx}`,
              category: 'oevrige',
              categoryLabel: 'Øvrige forvandlinger',
              projectTitle,
              title: projectTitle,
              photoType,
              badgeLabel: photoType === 'foer' ? 'FØR' : photoType === 'efter' ? 'EFTER' : 'Interiør',
              description: b.beskrivelse || (fallbackGallery ? (fallbackGallery.beskrivelse || '') : ''),
              sortering: typeof b.sortering === 'number' ? b.sortering : idx
            };
            extraImages.push(imgObj);
            allImgsList.push(imgObj);
          }
        });
      }

      if (fallbackGallery) {
        const fallbackRaw = fallbackGallery.images || fallbackGallery.billeder || [];
        const fallbackCaptions = parseImageCaptions(fallbackGallery.billedtekster, fallbackRaw.length);
        fallbackRaw.forEach((url, idx) => {
          if (!seenUrls.has(url)) {
            seenUrls.add(url);
            const photoType = getPhotoType(url);
            const cap = fallbackCaptions[idx];
            const projectTitle = cap?.title || (photoType === 'foer' ? 'Før folie' : photoType === 'efter' ? 'Efter folie' : 'Interiøropgave');
            const imgObj = {
              url,
              images: [url],
              imageCount: 1,
              isAlbum: false,
              id: `extra-${idx}`,
              category: 'oevrige',
              categoryLabel: 'Øvrige forvandlinger',
              projectTitle,
              title: projectTitle,
              photoType,
              badgeLabel: photoType === 'foer' ? 'FØR' : photoType === 'efter' ? 'EFTER' : 'Interiør',
              description: cap?.description || fallbackGallery.beskrivelse || '',
              sortering: idx
            };
            extraImages.push(imgObj);
            allImgsList.push(imgObj);
          }
        });
      }

      if (extraImages.length > 0) {
        byCat['oevrige'] = extraImages;
        catDefs.push({
          id: 'oevrige',
          label: 'Øvrige forvandlinger',
          sublabel: fallbackGallery?.beskrivelse || 'Andre udførte interiørprojekter',
          icon: 'fa-sparkles',
          count: extraImages.length
        });
      }
    } else if (fallbackGallery) {
      // Hvis kunden kun har ét samlet galleri i CMS
      const rawImages = fallbackGallery.images || fallbackGallery.billeder || [];
      const fallbackCaptions = parseImageCaptions(fallbackGallery.billedtekster, rawImages.length);
      const mappedImages = rawImages.map((url, idx) => {
        const photoType = getPhotoType(url);
        const cap = fallbackCaptions[idx];
        return {
          url,
          id: `interior-${idx}`,
          category: 'alle',
          categoryLabel: 'Interiørfolie',
          projectTitle: cap?.title || (photoType === 'foer' ? 'Før folie' : photoType === 'efter' ? 'Efter folie' : 'Udført interiøropgave'),
          photoType,
          badgeLabel: photoType === 'foer' ? 'FØR' : photoType === 'efter' ? 'EFTER' : 'Interiør',
          description: cap?.description || fallbackGallery.beskrivelse || ''
        };
      });
      allImgsList.push(...mappedImages);
    }

    // Sortér albummer efter sortering
    allImgsList.sort((a, b) => (a.sortering ?? 99) - (b.sortering ?? 99));

    // Sortér kategorierne i en fast, genkendelig rækkefølge
    const preferredOrder = [
      'interioerfolie-koekken',
      'interioerfolie-borde-moebler',
      'interioerfolie-erhverv',
      'oevrige'
    ];
    catDefs.sort((a, b) => {
      const idxA = preferredOrder.indexOf(a.id);
      const idxB = preferredOrder.indexOf(b.id);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;
      if (idxB !== -1) return 1;
      return 0;
    });

    // "Alle forvandlinger" placeres altid som første overblikskort
    const finalCategories = [
      {
        id: 'alle',
        label: 'Alle forvandlinger',
        sublabel: 'Samlet oversigt',
        icon: 'fa-layer-group',
        count: allImgsList.length
      },
      ...catDefs.map((c) => ({
        ...c,
        count: (byCat[c.id] || []).length
      }))
    ];

    return {
      categories: finalCategories,
      allImages: allImgsList,
      imagesByCategory: byCat
    };
  }, [galleries, dedicatedBilleder]);

  // Nulstil valgt kategori hvis den ikke længere findes
  useEffect(() => {
    if (activeCategory !== 'alle' && !categories.some((c) => c.id === activeCategory)) {
      setActiveCategory('alle');
    }
  }, [categories, activeCategory]);

  // Filtrerede billeder baseret på aktiv fane
  const filteredImages = useMemo(() => {
    if (activeCategory === 'alle') return allImages;
    return imagesByCategory[activeCategory] || allImages;
  }, [activeCategory, allImages, imagesByCategory]);

  // Paginering
  const totalImages = filteredImages.length;
  const totalPages = Math.ceil(totalImages / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const currentImages = filteredImages.slice(startIndex, startIndex + PAGE_SIZE);

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    setCurrentPage(1);
    setSelectedAlbumIndex(null);
    if (gallerySectionRef.current) {
      const rect = gallerySectionRef.current.getBoundingClientRect();
      if (rect.top < 0) {
        gallerySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    setSelectedAlbumIndex(null);
    if (gallerySectionRef.current) {
      gallerySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const selectedAlbum = selectedAlbumIndex !== null ? filteredImages[selectedAlbumIndex] : null;

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-9 w-9 animate-spin rounded-full border-4 border-[#245b53] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-16">
      {/* ========================================================================= */}
      {/* 1. INTERAKTIV FØR / EFTER SAMMENLIGNING (Before / After Slider)           */}
      {/* ========================================================================= */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 space-y-6 shadow-xs">
        {/* Enkel top med titel og fanevælger */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              Før & efter forvandling
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Træk i skyderen for at se forskellen med interiørfolie
            </p>
          </div>

          {/* Vælg case faner */}
          {beforeAfterCases.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5 p-1 bg-gray-100 rounded-xl">
              {beforeAfterCases.map((item, idx) => (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveCaseIndex(idx);
                    setSliderPosition(50);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    activeCaseIndex === idx
                      ? 'bg-white text-[#245b53] shadow-xs font-semibold'
                      : 'text-gray-600 hover:text-gray-950 hover:bg-white/60'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Billedskyder og case info */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          {/* Slider Container */}
          <div className="lg:col-span-8">
            <div
              ref={sliderContainerRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseEnter={handleContainerMouseMove}
              onMouseMoveCapture={handleContainerMouseMove}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              className="relative aspect-[4/3] sm:aspect-[16/10] w-full overflow-hidden rounded-xl border border-gray-200 shadow-sm select-none cursor-ew-resize group bg-gray-100"
              style={{ touchAction: 'none' }}
              title="Træk for at sammenligne før og efter"
            >
              {/* EFTER BILLEDE */}
              <img
                src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${activeCase.afterImage.replace('https://assets.auxodev.dk/', '')}`}
                alt={activeCase.afterAlt}
                className="absolute inset-0 h-full w-full object-cover pointer-events-none"
                draggable={false}
              />

              {/* FØR BILLEDE */}
              <div
                className="absolute inset-0 overflow-hidden pointer-events-none transition-none"
                style={{ width: `${sliderPosition}%` }}
              >
                <div className="relative h-full w-full">
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/${activeCase.beforeImage.replace('https://assets.auxodev.dk/', '')}`}
                    alt={activeCase.beforeAlt}
                    className="absolute inset-0 h-full max-w-none object-cover pointer-events-none"
                    style={{
                      width: sliderContainerRef.current ? `${sliderContainerRef.current.clientWidth}px` : '100%',
                      height: '100%'
                    }}
                    draggable={false}
                  />
                </div>
              </div>

              {/* Mærkat: FØR */}
              <div className="absolute top-3 left-3 z-20 pointer-events-none">
                <span className="inline-block rounded-md bg-black/70 backdrop-blur-xs px-2.5 py-1 text-xs font-semibold text-white tracking-wide">
                  Før
                </span>
              </div>

              {/* Mærkat: EFTER */}
              <div className="absolute top-3 right-3 z-20 pointer-events-none">
                <span className="inline-block rounded-md bg-[#245b53]/90 backdrop-blur-xs px-2.5 py-1 text-xs font-semibold text-white tracking-wide">
                  Efter
                </span>
              </div>

              {/* SKYDELINJE & HÅNDTAG */}
              <div
                className="absolute top-0 bottom-0 z-30 pointer-events-none transition-none"
                style={{ left: `${sliderPosition}%` }}
              >
                <div className="absolute top-0 bottom-0 -left-[1px] w-[2px] bg-white shadow-md" />
                <div className="absolute top-1/2 -left-4 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[#245b53] shadow-md border border-gray-200">
                  <i className="fa-solid fa-arrows-left-right text-xs" aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>

          {/* Case Detaljer (Højre side) */}
          <div className="lg:col-span-4 space-y-4">
            <div>
              {(activeCase.tag || activeCase.location) && (
                <div className="text-xs font-semibold uppercase tracking-wider text-[#245b53] mb-1">
                  {[activeCase.tag, activeCase.location].filter(Boolean).join(' • ')}
                </div>
              )}
              <h4 className="text-lg sm:text-xl font-bold text-gray-900">
                {activeCase.title}
              </h4>
            </div>

            {activeCase.description && (
              <p className="text-sm text-gray-600 leading-relaxed">
                {activeCase.description}
              </p>
            )}

            {/* Fordele */}
            {activeCase.benefits && activeCase.benefits.length > 0 && (
              <ul className="space-y-2 pt-2 border-t border-gray-100 text-sm text-gray-700">
                {activeCase.benefits.map((benefit, bIdx) => (
                  <li key={bIdx} className="flex items-center gap-2">
                    <i className="fa-solid fa-check text-[#245b53] text-xs shrink-0" aria-hidden="true" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="pt-2">
              <a
                href="#kontakt"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#245b53] hover:text-[#1b453f] transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  const contactEl = document.getElementById('kontakt') || document.querySelector('footer');
                  if (contactEl) contactEl.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                <span>Få et tilbud på en forvandling</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 2. STRUKTURERET ALBUM- & PROJEKT-OPDELING (Fremtidssikret galleri)        */}
      {/* ========================================================================= */}
      <div ref={gallerySectionRef} className="space-y-8 pt-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="text-xs font-bold uppercase tracking-wider text-[#245b53]">
              Fremtidssikret referencegalleri
            </span>
            <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900">
              Udforsk vores projekter efter kategori
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Opdelt i konkrete cases og projekter, så du hurtigt kan finde netop den type overflade, du overvejer.
            </p>
          </div>

          {/* Aktiv kategori status */}
          <div className="text-xs font-semibold text-gray-500 bg-gray-100 px-3 py-1.5 rounded-full self-start md:self-auto shrink-0">
            Viser {filteredImages.length} billeder {activeCategory !== 'alle' && `• ${categories.find(c => c.id === activeCategory)?.label}`}
          </div>
        </div>

        {/* Projekt-kategori knapper / tabs */}
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${categories.length > 4 ? 'lg:grid-cols-5 md:grid-cols-3' : categories.length === 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
          {categories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryChange(cat.id)}
                className={`p-4 rounded-2xl text-left transition-all border cursor-pointer flex flex-col justify-between gap-3 ${
                  isActive
                    ? 'bg-[#245b53] text-white border-[#245b53] shadow-md ring-2 ring-[#245b53]/20'
                    : 'bg-white text-gray-800 border-gray-200 hover:border-gray-300 hover:bg-gray-50/80 shadow-2xs'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className={`flex h-9 w-9 items-center justify-center rounded-xl ${
                    isActive ? 'bg-white/20 text-[#deecd4]' : 'bg-gray-100 text-[#245b53]'
                  }`}>
                    <i className={`fa-solid ${cat.icon} text-sm`} aria-hidden="true" />
                  </div>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {cat.count} stk
                  </span>
                </div>

                <div>
                  <div className={`text-sm font-bold ${isActive ? 'text-white' : 'text-gray-900'}`}>
                    {cat.label}
                  </div>
                  <div className={`text-xs truncate ${isActive ? 'text-emerald-100' : 'text-gray-500'}`}>
                    {cat.sublabel}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Billedgitter med albummer og forvandlinger */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 pt-2">
          {currentImages.map((item, idx) => {
            const globalIndex = startIndex + idx;
            const isMulti = item.isAlbum || (item.imageCount && item.imageCount > 1);
            const imageCount = item.imageCount || (item.images ? item.images.length : 1);

            return (
              <div
                key={item.id || globalIndex}
                onClick={() => setSelectedAlbumIndex(globalIndex)}
                className="group relative flex flex-col justify-between overflow-hidden rounded-2xl sm:rounded-3xl bg-white border border-gray-200/90 shadow-xs hover:shadow-xl transition-all duration-300 hover:-translate-y-1 hover:border-[#325d52]/50 cursor-pointer"
              >
                {/* Stacked effekt hvis der er flere billeder i albummet */}
                {isMulti && (
                  <div
                    className="absolute inset-x-3 -top-1.5 h-3 rounded-t-2xl bg-gray-300/70 border border-gray-300/80 -z-10 group-hover:-top-2 transition-all duration-300"
                    aria-hidden="true"
                  />
                )}

                {/* Cover billede */}
                <div className="relative aspect-[4/3] w-full overflow-hidden bg-gray-100">
                  <img
                    src={imageUrl(item.url, 'width=700,height=525,fit=cover,quality=85,format=auto')}
                    alt={`${item.title || item.projectTitle} - Forsidebillede`}
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                    loading="lazy"
                  />

                  {/* Subtilt gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-80 group-hover:opacity-60 transition-opacity" />

                  {/* Badge: Antal billeder */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-black/75 px-3 py-1 text-xs font-semibold text-white backdrop-blur-md border border-white/20 shadow-md group-hover:bg-[#325d52] group-hover:border-[#deecd4]/40 transition-colors">
                      <i className={isMulti ? 'fa-solid fa-layer-group text-[#deecd4]' : 'fa-solid fa-camera text-[#deecd4]'} />
                      <span>
                        {imageCount} {imageCount === 1 ? 'billede' : 'billeder'}
                      </span>
                    </span>
                  </div>

                  {/* Hurtig forstørrelsesindikator ved hover */}
                  <div className="absolute bottom-3 right-3 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/90 text-[#1b453f] shadow-lg backdrop-blur-xs">
                      <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
                    </span>
                  </div>
                </div>

                {/* Tekst og albumoplysninger */}
                <div className="flex flex-1 flex-col justify-between p-4 sm:p-5">
                  <div>
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 group-hover:text-[#325d52] transition-colors line-clamp-1">
                      {item.title || item.projectTitle}
                    </h3>
                    {item.description ? (
                      <p className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed">
                        {item.description}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-gray-400 italic">
                        Se billeder og udført arbejde
                      </p>
                    )}
                  </div>

                  {/* Handlingsknap i bunden */}
                  <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-xs font-semibold text-[#325d52]">
                    <span className="inline-flex items-center gap-1.5">
                      <i className="fa-solid fa-folder-open text-[#245b53]" />
                      <span>{isMulti ? `Åbn album (${imageCount} fotos)` : 'Se stort billede'}</span>
                    </span>
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#deecd4] text-[#1b453f] transition-transform group-hover:translate-x-1">
                      <i className="fa-solid fa-chevron-right text-[10px]" />
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Paginering */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-6">
            <div className="text-xs text-gray-500 font-medium">
              Viser {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, totalImages)} af {totalImages} billeder
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                <i className="fa-solid fa-arrow-left text-[10px]" />
                Forrige
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`h-8 w-8 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                      currentPage === pageNum
                        ? 'bg-[#245b53] text-white shadow-xs'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-2xs hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
              >
                Næste
                <i className="fa-solid fa-arrow-right text-[10px]" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ========================================================================= */}
      {/* 3. LIGHTBOX / ALBUM MODAL TIL FULD SKÆRM VISNING                          */}
      {/* ========================================================================= */}
      <AlbumModal
        isOpen={selectedAlbumIndex !== null}
        album={
          selectedAlbum
            ? {
                ...selectedAlbum,
                title: selectedAlbum.title || selectedAlbum.projectTitle,
                description: selectedAlbum.description,
                category: selectedAlbum.categoryLabel || selectedAlbum.category,
                url: selectedAlbum.url,
                images: selectedAlbum.images && selectedAlbum.images.length > 0
                  ? selectedAlbum.images
                  : [selectedAlbum.url]
              }
            : null
        }
        onClose={() => setSelectedAlbumIndex(null)}
        onPrevAlbum={
          selectedAlbumIndex !== null && selectedAlbumIndex > 0
            ? () => setSelectedAlbumIndex(selectedAlbumIndex - 1)
            : null
        }
        onNextAlbum={
          selectedAlbumIndex !== null && selectedAlbumIndex < filteredImages.length - 1
            ? () => setSelectedAlbumIndex(selectedAlbumIndex + 1)
            : null
        }
        prevAlbumTitle={
          selectedAlbumIndex !== null && selectedAlbumIndex > 0
            ? (filteredImages[selectedAlbumIndex - 1]?.title || filteredImages[selectedAlbumIndex - 1]?.projectTitle)
            : ''
        }
        nextAlbumTitle={
          selectedAlbumIndex !== null && selectedAlbumIndex < filteredImages.length - 1
            ? (filteredImages[selectedAlbumIndex + 1]?.title || filteredImages[selectedAlbumIndex + 1]?.projectTitle)
            : ''
        }
      />
    </div>
  );
}
